import {
  AfterViewInit,
  ContentChild,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  Output,
  Renderer2
} from '@angular/core';
import {
  DndEvent,
  DragDropData,
  getDirectChildElement,
  getDropData,
  shouldPositionPlaceholderBeforeElement
} from './dnd-utils';
import { getDndType, getDropEffect, isExternalDrag, setDropEffect } from './dnd-state';
import { DropEffect, EffectAllowed } from './dnd-types';

export interface DndDropEvent {
  event:DragEvent;
  dropEffect:DropEffect;
  isExternal:boolean;
  data?:any;
  index?:number;
}
export class DndPlaceholderRefDirective {
/**
 * @param {?} elementRef
 */
constructor(
public readonly elementRef:ElementRef ) {
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: "[dndPlaceholderRef]"
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
];
}

function DndPlaceholderRefDirective_tsickle_Closure_declarations() {
/** @type {?} */
DndPlaceholderRefDirective.decorators;
/**
 * @nocollapse
 * @type {?}
 */
DndPlaceholderRefDirective.ctorParameters;
/** @type {?} */
DndPlaceholderRefDirective.prototype.elementRef;
}

export class DndDropzoneDirective implements AfterViewInit, OnDestroy {

  
  dndDropzone?:string[];

  
  dndEffectAllowed:EffectAllowed;

  
  dndAllowExternal:boolean = false;

  
  dndHorizontal:boolean = false;

  
  dndDragoverClass:string = "dndDragover";

  
  dndDropzoneDisabledClass = "dndDropzoneDisabled";

  
  readonly dndDragover:EventEmitter<DragEvent> = new EventEmitter<DragEvent>();

  
  readonly dndDrop:EventEmitter<DndDropEvent> = new EventEmitter<DndDropEvent>();
private readonly dndPlaceholderRef?:DndPlaceholderRefDirective;
private placeholder:Element | null = null;
private disabled:boolean = false;
private readonly dragEventHandler: (event: DragEvent) => void = (event: DragEvent) => this.onDragOver(event);
/**
 * @param {?} value
 * @return {?}
 */
set dndDisableIf( value:boolean ) {

    this.disabled = !!value;

    if( this.disabled ) {

      this.renderer.addClass( this.elementRef.nativeElement, this.dndDropzoneDisabledClass );
    }
    else {

      this.renderer.removeClass( this.elementRef.nativeElement, this.dndDropzoneDisabledClass );
    }
  }
/**
 * @param {?} ngZone
 * @param {?} elementRef
 * @param {?} renderer
 */
constructor(
private ngZone: NgZone,
private elementRef:ElementRef,
private renderer:Renderer2 ) {
  }
/**
 * @return {?}
 */
ngAfterViewInit():void {

    this.placeholder = this.tryGetPlaceholder();

    if( this.placeholder !== null ) {

      this.placeholder.remove();
    }
    this.ngZone.runOutsideAngular(() => {
      this.elementRef.nativeElement.addEventListener("dragover", this.dragEventHandler)
    } );
  }
/**
 * @return {?}
 */
ngOnDestroy(): void {
    this.elementRef.nativeElement.removeEventListener("dragover", this.dragEventHandler);
  }
/**
 * @param {?} event
 * @return {?}
 */
onDragEnter( event:DndEvent ) {

    // check if another dropzone is activated
    if( event._dndDropzoneActive === true ) {

      this.cleanupDragoverState();
      return;
    }

    // set as active if the target element is inside this dropzone
    if( typeof event._dndDropzoneActive === "undefined" ) {

      const /** @type {?} */ newTarget = document.elementFromPoint( event.clientX, event.clientY );

      if( this.elementRef.nativeElement.contains( newTarget ) ) {

        event._dndDropzoneActive = true;
      }
    }

    // check if this drag event is allowed to drop on this dropzone
    const /** @type {?} */ type = getDndType( event );
    if( this.isDropAllowed( type ) === false ) {

      return;
    }

    // allow the dragenter
    event.preventDefault();
  }
/**
 * @param {?} event
 * @return {?}
 */
onDragOver( event:DragEvent ) {

    // check if this drag event is allowed to drop on this dropzone
    const /** @type {?} */ type = getDndType( event );
    if( this.isDropAllowed( type ) === false ) {

      return;
    }

    this.checkAndUpdatePlaceholderPosition( event );

    const /** @type {?} */ dropEffect = getDropEffect( event, this.dndEffectAllowed );

    if( dropEffect === "none" ) {

      this.cleanupDragoverState();
      return;
    }

    // allow the dragover
    event.preventDefault();

    // set the drop effect
    setDropEffect( event, dropEffect );

    this.dndDragover.emit( event );

    this.renderer.addClass( this.elementRef.nativeElement, this.dndDragoverClass );

    event.stopPropagation();
  }
/**
 * @param {?} event
 * @return {?}
 */
onDrop( event:DragEvent ) {

    try {

      // check if this drag event is allowed to drop on this dropzone
      const /** @type {?} */ type = getDndType( event );
      if( this.isDropAllowed( type ) === false ) {

        return;
      }

      //  不使用这个功能, 在组件中更新移动元素位置
      const /** @type {?} */ data:DragDropData = getDropData( event, isExternalDrag() );

      if( this.isDropAllowed( data.type ) === false ) {

        return;
      }

      // signal custom drop handling
      event.preventDefault();

      const /** @type {?} */ dropEffect = getDropEffect( event );

      setDropEffect( event, dropEffect );

      if( dropEffect === "none" ) {

        return;
      }

      const /** @type {?} */ dropIndex = this.getPlaceholderIndex();
      this.dndDrop.emit( {
        event: event,
        dropEffect: dropEffect,
        isExternal: isExternalDrag(),
        data: data.data,
        index: dropIndex
      } );

      event.stopPropagation();

    }
    finally {

      this.cleanupDragoverState();
    }
  }
/**
 * @param {?} event
 * @return {?}
 */
onDragLeave( event:DndEvent ) {

    // check if still inside this dropzone and not yet handled by another dropzone
    if( typeof event._dndDropzoneActive === "undefined" ) {

      const /** @type {?} */ newTarget = document.elementFromPoint( event.clientX, event.clientY );

      if( this.elementRef.nativeElement.contains( newTarget ) ) {

        event._dndDropzoneActive = true;
        return;
      }
    }

    this.cleanupDragoverState();

    // cleanup drop effect when leaving dropzone
    setDropEffect( event, "none" );
  }
/**
 * @return {?}
 */
private tryGetPlaceholder():Element | null {

    if( typeof this.dndPlaceholderRef !== "undefined" ) {

      return /** @type {?} */(( this.dndPlaceholderRef.elementRef.nativeElement as Element));
    }

    // TODO nasty workaround needed because if ng-container / template is used @ContentChild() or DI will fail because
    // of wrong context see angular bug https://github.com/angular/angular/issues/13517
    return this.elementRef.nativeElement.querySelector( "[dndPlaceholderRef]" );
  }
/**
 * @param {?=} type
 * @return {?}
 */
private isDropAllowed( type?:string ):boolean {

    // dropzone is disabled -> deny it
    if( this.disabled === true ) {

      return false;
    }

    // if drag did not start from our directive
    // and external drag sources are not allowed -> deny it
    if( isExternalDrag() === true
      && this.dndAllowExternal === false ) {

      return false;
    }

    // no filtering by types -> allow it
    if( !this.dndDropzone ) {

      return true;
    }

    // no type set -> allow it
    if( !type ) {

      return true;
    }

    if( Array.isArray( this.dndDropzone ) === false ) {

      throw new Error( "dndDropzone: bound value to [dndDropzone] must be an array!" );
    }

    // if dropzone contains type -> allow it
    return this.dndDropzone.indexOf( type ) !== -1;
  }
/**
 * @param {?} event
 * @return {?}
 */
private checkAndUpdatePlaceholderPosition( event:DragEvent ):void {

    if( this.placeholder === null ) {

      return;
    }

    // make sure the placeholder is in the DOM
    if( this.placeholder.parentNode !== this.elementRef.nativeElement ) {

      this.renderer.appendChild( this.elementRef.nativeElement, this.placeholder );
    }

    // update the position if the event originates from a child element of the dropzone
    const /** @type {?} */ directChild = getDirectChildElement( this.elementRef.nativeElement, /** @type {?} */(( event.target as Element)) );

    // early exit if no direct child or direct child is placeholder
    if( directChild === null
      || directChild === this.placeholder ) {

      return;
    }

    const /** @type {?} */ positionPlaceholderBeforeDirectChild = shouldPositionPlaceholderBeforeElement( event, directChild, this.dndHorizontal );

    if( positionPlaceholderBeforeDirectChild ) {

      // do insert before only if necessary
      if( directChild.previousSibling !== this.placeholder ) {

        this.renderer.insertBefore( this.elementRef.nativeElement, this.placeholder, directChild );
      }
    }
    else {

      // do insert after only if necessary
      if( directChild.nextSibling !== this.placeholder ) {

        this.renderer.insertBefore( this.elementRef.nativeElement, this.placeholder, directChild.nextSibling );
      }
    }
  }
/**
 * @return {?}
 */
private getPlaceholderIndex():number | undefined {

    if( this.placeholder === null ) {

      return undefined;
    }

    const /** @type {?} */ element = /** @type {?} */(( this.elementRef.nativeElement as HTMLElement));

    return Array.prototype.indexOf.call( element.children, this.placeholder );
  }
/**
 * @return {?}
 */
private cleanupDragoverState() {

    this.renderer.removeClass( this.elementRef.nativeElement, this.dndDragoverClass );

    if( this.placeholder !== null ) {

      this.placeholder.remove();
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: "[dndDropzone]"
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: NgZone, },
{type: ElementRef, },
{type: Renderer2, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'dndDropzone': [{ type: Input },],
'dndEffectAllowed': [{ type: Input },],
'dndAllowExternal': [{ type: Input },],
'dndHorizontal': [{ type: Input },],
'dndDragoverClass': [{ type: Input },],
'dndDropzoneDisabledClass': [{ type: Input },],
'dndDragover': [{ type: Output },],
'dndDrop': [{ type: Output },],
'dndPlaceholderRef': [{ type: ContentChild, args: [DndPlaceholderRefDirective, ] },],
'dndDisableIf': [{ type: Input },],
'onDragEnter': [{ type: HostListener, args: ["dragenter", [ "$event" ], ] },],
'onDrop': [{ type: HostListener, args: ["drop", [ "$event" ], ] },],
'onDragLeave': [{ type: HostListener, args: ["dragleave", [ "$event" ], ] },],
};
}

function DndDropzoneDirective_tsickle_Closure_declarations() {
/** @type {?} */
DndDropzoneDirective.decorators;
/**
 * @nocollapse
 * @type {?}
 */
DndDropzoneDirective.ctorParameters;
/** @type {?} */
DndDropzoneDirective.propDecorators;
/** @type {?} */
DndDropzoneDirective.prototype.dndDropzone;
/** @type {?} */
DndDropzoneDirective.prototype.dndEffectAllowed;
/** @type {?} */
DndDropzoneDirective.prototype.dndAllowExternal;
/** @type {?} */
DndDropzoneDirective.prototype.dndHorizontal;
/** @type {?} */
DndDropzoneDirective.prototype.dndDragoverClass;
/** @type {?} */
DndDropzoneDirective.prototype.dndDropzoneDisabledClass;
/** @type {?} */
DndDropzoneDirective.prototype.dndDragover;
/** @type {?} */
DndDropzoneDirective.prototype.dndDrop;
/** @type {?} */
DndDropzoneDirective.prototype.dndPlaceholderRef;
/** @type {?} */
DndDropzoneDirective.prototype.placeholder;
/** @type {?} */
DndDropzoneDirective.prototype.disabled;
/** @type {?} */
DndDropzoneDirective.prototype.dragEventHandler;
/** @type {?} */
DndDropzoneDirective.prototype.ngZone;
/** @type {?} */
DndDropzoneDirective.prototype.elementRef;
/** @type {?} */
DndDropzoneDirective.prototype.renderer;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
